/* eslint-disable sentence-case/sentence-case */
import React, { useEffect, useState } from 'react'
import { Box, Button, Divider, InputAdornment, Typography } from '@mui/material'
import {
    useListContext,
    FunctionField,
    TextInput,
    ListBase,
    Datagrid,
} from 'react-admin'
import { Search } from '@mui/icons-material'
import { FormProvider, useForm } from 'react-hook-form'
import RunExpand from '../Runs/RunExpand'
import { EmptyState, IdField } from '../../components/common'
import ListPagination from '../List/ListPagination'
import { formatDate } from '@thefront/pandipackV2/src/utils'
import { useThemeConfig } from '../../themeConfig'

const FILTER_KEYS = ['search']

const LogFilter = ({ loading, setLoading, dirty, setDirty }) => {
    const {
        data,
        isFetching,
        refetch,
        filterValues,
        setFilters,
    } = useListContext()
    const [initialLoadComplete, setInitialLoadComplete] = useState(false)

    const methods = useForm({
        defaultValues: filterValues,
    })

    // Fetch data on page load if the search is cached in the URL
    useEffect(() => {
        // if data is not null we want to use the cached data instead of refetching
        if (filterValues.search?.length && !data) {
            refetch()
        }
        setInitialLoadComplete(true)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const search = () => {
        setLoading(true)
        const filtersUnchanged = FILTER_KEYS.every(
            (key) => methods.getValues()[key] === filterValues[key]
        )
        if (filtersUnchanged) {
            refetch()
        } else {
            // setFilters() triggers the useEffect below
            // if refetch() is called immediately after setFilters(), filters will not be set in time
            setFilters(methods.getValues(), undefined, false)
        }
        setDirty(false)
    }

    useEffect(() => {
        // initialLoadComplete prevents this from running on initial render
        if (filterValues.search?.length && initialLoadComplete) {
            refetch()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterValues])

    // reset loading state when fetch completes
    useEffect(() => {
        if (!isFetching) setLoading(false)
    }, [setLoading, isFetching])

    // handle pressing enter in the search input
    const handleKeyDown = (event) => {
        if (
            event.key === 'Enter' &&
            dirty &&
            methods.getValues().search?.length
        ) {
            search()
        }
    }

    const formValues = methods.watch()

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                marginY: 1,
                marginLeft: 1,
            }}
        >
            <FormProvider {...methods}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 2,
                    }}
                >
                    <TextInput
                        source="search"
                        variant="outlined"
                        resettable={true}
                        label=""
                        fullWidth
                        placeholder="Search across runs"
                        sx={(theme) => ({
                            width: '600px',
                            '& .MuiInputBase-root': {
                                height: '38px',
                                borderRadius: 3,
                                width: 'auto',
                                '& > fieldset': {
                                    border:
                                        '2px solid ' +
                                        theme.CustomColors.bluegrey,
                                },
                                '&:hover': {
                                    '& > fieldset': {
                                        borderColor: 'primary.main',
                                    },
                                },
                            },
                            '& .MuiFormHelperText-root': {
                                position: 'absolute',
                            },
                        })}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search
                                        fontSize="small"
                                        color="rgba(102, 102, 102, 1)"
                                    />
                                </InputAdornment>
                            ),
                            sx: {
                                '&::placeholder': {
                                    color: 'rgba(102, 102, 102, 1)',
                                    opacity: 1,
                                },
                            },
                        }}
                        onChange={(e) => {
                            setDirty(true)
                        }}
                        onKeyDown={handleKeyDown}
                    />
                    <Button
                        variant="contained"
                        sx={{ marginTop: '3px' }}
                        onClick={search}
                        disabled={!formValues.search?.length || !dirty}
                        loading={loading}
                    >
                        Search
                    </Button>
                </Box>
            </FormProvider>
            <Divider sx={{ marginY: 2 }} />
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    visibility:
                        filterValues.search && !loading && data
                            ? 'visible'
                            : 'hidden',
                }}
            >
                <Typography sx={{ fontStyle: 'italic' }}>
                    Showing results for:
                </Typography>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    <Typography
                        sx={(theme) => ({
                            background: theme.CustomColors.lightbluegrey,
                            paddingY: '3px',
                            paddingX: 1.5,
                            borderRadius: 10,
                            border: '1px solid ' + theme.CustomColors.bluegrey,
                        })}
                    >
                        Query: <strong>{filterValues.search}</strong>
                    </Typography>
                </Box>
            </Box>
        </Box>
    )
}

const dataGridCSS = {
    headerCell: {
        height: '48px',
        fontSize: '20px',
        fontWeight: 'bold',
        color: 'black',
        paddingLeft: '8px',
    },
    rowCell: {
        height: '56px',
        textAlign: 'left',
        textTransform: 'capitalize',
        font: 'Roboto Regular',
        paddingLeft: '6px',
        fontSize: '16px',
    },
}

export const LogDatagrid = ({ loading, dirty, ...rest }) => {
    const { filterValues } = useListContext()
    const { configs } = useThemeConfig()
    return (
        <>
            {loading ? (
                <EmptyState emptyStateText="Searching..." />
            ) : (
                <Datagrid
                    className={'pandium-logs-list'}
                    {...rest}
                    expand={(props) => (
                        <RunExpand
                            {...props}
                            disableLogPolling={true}
                            query={filterValues?.search}
                        />
                    )}
                    expandSingle
                    rowClick="expand"
                    bulkActionButtons={false}
                    empty={
                        <EmptyState
                            emptyStateText={
                                filterValues?.search?.length
                                    ? 'No run logs found matching the query.'
                                    : dirty
                                    ? 'Press enter or click search to see results.'
                                    : 'Enter a query to search run logs.'
                            }
                        />
                    }
                    sx={{
                        '& .RaDatagrid-headerCell': dataGridCSS.headerCell,
                        '& .RaDatagrid-rowCell': dataGridCSS.rowCell,
                        '& .RaDatagrid-expandIconCell': {
                            '& .MuiIconButton-root': {
                                color: 'primary.main',
                                fontSize: '1.5rem',
                            },
                        },
                    }}
                >
                    <IdField source="runName" sortable={false} label="ID" />
                    <FunctionField
                        fontSize="16px"
                        render={(record) =>
                            formatDate(
                                record.timestamp,
                                false,
                                configs.dateFormat
                            )
                        }
                        label="Timestamp"
                        sortBy="timestamp"
                        sortByOrder="DESC"
                    />
                    <FunctionField
                        source="textPayloads"
                        label="Line Matches"
                        sortable={false}
                        render={(record) => {
                            return (
                                <Box sx={{ marginY: 1 }}>
                                    <Box
                                        sx={(theme) => ({
                                            backgroundColor: '#FBFCFF',
                                            padding: '4px',
                                            borderRadius: 1,
                                            border:
                                                '1px solid' +
                                                theme.CustomColors.bluegrey,
                                        })}
                                    >
                                        {record.textPayloads
                                            .slice(0, 4)
                                            .map((text, index) => (
                                                <Typography
                                                    key={index}
                                                    sx={{ fontSize: '14px' }}
                                                    fontFamily="Monospace"
                                                >
                                                    {text}
                                                </Typography>
                                            ))}
                                    </Box>
                                    {record.textPayloads.length > 4 && (
                                        <Typography fontStyle="italic">
                                            + {record.textPayloads.length - 4}{' '}
                                            more ...
                                        </Typography>
                                    )}
                                </Box>
                            )
                        }}
                    />
                </Datagrid>
            )}
        </>
    )
}

export default function LogList({ tenant }) {
    const [dirty, setDirty] = useState(false)
    const [loading, setLoading] = useState(false)

    return (
        <ListBase
            resource="logs"
            storeKey={`log-${tenant.id}`}
            perPage={25}
            queryOptions={{
                enabled: false,
            }}
            filter={{ tenant_id: tenant.id }}
            sort={{ field: 'timestamp', order: 'DESC' }}
        >
            <LogFilter
                loading={loading}
                setLoading={setLoading}
                dirty={dirty}
                setDirty={setDirty}
            />
            <LogDatagrid loading={loading} dirty={dirty} />
            <ListPagination />
        </ListBase>
    )
}
